import { fetchContactsBySearch, type ContactSearchScope } from "./contactSearchCache";
import type { ContactSummary } from "@/types/entities";

export type { ContactSearchScope };

export const CONTACT_UNIQUENESS_MESSAGES = {
  emailDuplicate: "Email already exists.",
  phoneDuplicate: "Mobile number already exists.",
} as const;

export interface ContactInlineValidationErrors {
  email: string;
  mobilePhone: string;
  email2?: string;
  mobilePhone2?: string;
}

export const EMPTY_CONTACT_INLINE_ERRORS: ContactInlineValidationErrors = {
  email: "",
  mobilePhone: "",
  email2: "",
  mobilePhone2: "",
};

export function hasContactInlineValidationErrors(errors: ContactInlineValidationErrors): boolean {
  return Boolean(
    errors.email || errors.mobilePhone || errors.email2 || errors.mobilePhone2,
  );
}

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MIN_PHONE_DIGITS_FOR_UNIQUENESS = 8;
export const CONTACT_UNIQUENESS_DEBOUNCE_MS = 500;

export function normalizePhoneForCompare(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, "").trim();
}

export function normalizeEmailForCompare(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

export function countPhoneDigits(value: string): number {
  return value.replace(/\D/g, "").length;
}

export function isValidEmailForUniquenessCheck(value: string): boolean {
  const trimmed = value.trim();
  return Boolean(trimmed) && EMAIL_FORMAT_REGEX.test(trimmed);
}

export function isValidPhoneForUniquenessCheck(value: string): boolean {
  return countPhoneDigits(value) >= MIN_PHONE_DIGITS_FOR_UNIQUENESS;
}

export function isDuplicateContactMatch(
  match: ContactSummary | null,
  excludeContactId?: string | null,
): boolean {
  if (!match) return false;
  if (!excludeContactId) return true;
  return match.id !== excludeContactId;
}

export function contactSummaryMatchesPhone(contact: ContactSummary, phone: string): boolean {
  if (!isValidPhoneForUniquenessCheck(phone)) return false;
  const normalized = normalizePhoneForCompare(phone);
  const contactPhone = normalizePhoneForCompare(contact.mobilePhone);
  return Boolean(contactPhone) && contactPhone === normalized;
}

export function contactSummaryMatchesEmail(contact: ContactSummary, email: string): boolean {
  if (!isValidEmailForUniquenessCheck(email)) return false;
  const normalized = normalizeEmailForCompare(email);
  const contactEmail = normalizeEmailForCompare(contact.email);
  return Boolean(contactEmail) && contactEmail === normalized;
}

export interface ContactUniquenessSearchOptions {
  signal?: AbortSignal;
  scope?: ContactSearchScope;
}

export async function findContactByPhone(
  phone: string,
  options?: ContactUniquenessSearchOptions,
): Promise<ContactSummary | null> {
  const trimmed = phone.trim();
  if (!trimmed || !isValidPhoneForUniquenessCheck(trimmed)) return null;
  const contacts = await fetchContactsBySearch(trimmed, options);
  if (options?.signal?.aborted) return null;
  return contacts.find((c) => contactSummaryMatchesPhone(c, trimmed)) ?? null;
}

export async function findContactByEmail(
  email: string,
  options?: ContactUniquenessSearchOptions,
): Promise<ContactSummary | null> {
  const trimmed = email.trim();
  if (!trimmed || !isValidEmailForUniquenessCheck(trimmed)) return null;
  const searchQuery = normalizeEmailForCompare(trimmed);
  const contacts = await fetchContactsBySearch(searchQuery, options);
  if (options?.signal?.aborted) return null;
  return contacts.find((c) => contactSummaryMatchesEmail(c, trimmed)) ?? null;
}
